import axios from 'axios';
import { API_URL } from '../../../config';

let gptProvider = 'openrouter';

export function setGptProvider(provider) {
    gptProvider = provider === 'nanogpt' ? 'nanogpt' : 'openrouter';
}

export function getGptProvider() {
    return gptProvider;
}

const gptApi = axios.create({
    baseURL: API_URL,
    headers: {
        Accept: 'application/json',
    },
});

function readHeader(headers, name) {
    if (!headers) return undefined;
    if (typeof headers.get === 'function') return headers.get(name) || undefined;
    return headers[name] || headers[name.toLowerCase()] || undefined;
}

gptApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (!readHeader(config.headers, 'X-GPT-Provider')) {
        config.headers['X-GPT-Provider'] = gptProvider;
    }
    const provider = readHeader(config.headers, 'X-GPT-Provider') || gptProvider;
    config.params = { ...(config.params || {}), provider };
    return config;
});

gptApi.interceptors.response.use(
    (r) => r,
    (error) => {
        const payload = error.response?.data?.error || error.response?.data?.detail;
        if (payload && typeof payload === 'object' && payload.message) {
            error.gptError = payload;
        }
        return Promise.reject(error);
    }
);

export function fileDownloadUrl(fileId) {
    return `${API_URL}/api/files/${fileId}`;
}

export async function fetchMe() {
    const { data } = await gptApi.get('/api/auth/me');
    return data;
}

export async function fetchModels(provider) {
    const { data } = await gptApi.get('/api/models', {
        headers: provider ? { 'X-GPT-Provider': provider } : undefined,
    });
    return data;
}

export async function fetchImageModels() {
    const { data } = await gptApi.get('/api/image-models');
    return data;
}

export async function fetchChats() {
    const { data } = await gptApi.get('/api/chats');
    return data;
}

export async function createChat(payload = {}) {
    const { data } = await gptApi.post('/api/chats', payload);
    return data;
}

export async function updateChat(chatId, payload) {
    const { data } = await gptApi.patch(`/api/chats/${chatId}`, payload);
    return data;
}

export async function deleteChat(chatId) {
    const { data } = await gptApi.delete(`/api/chats/${chatId}`);
    return data;
}

export async function fetchFolders() {
    const { data } = await gptApi.get('/api/folders');
    return data;
}

export async function createFolder(payload = {}) {
    const { data } = await gptApi.post('/api/folders', payload);
    return data;
}

export async function updateFolder(folderId, payload) {
    const { data } = await gptApi.patch(`/api/folders/${folderId}`, payload);
    return data;
}

export async function deleteFolder(folderId) {
    const { data } = await gptApi.delete(`/api/folders/${folderId}`);
    return data;
}

export async function fetchMessages(chatId, { limit = 40, before } = {}) {
    const { data } = await gptApi.get(`/api/chats/${chatId}/messages`, {
        params: {
            limit,
            before: before || undefined,
        },
    });
    return data;
}

export async function deleteMessage(chatId, messageId) {
    const { data } = await gptApi.delete(`/api/chats/${chatId}/messages/${messageId}`);
    return data;
}

export async function fetchCredits(provider) {
    const resolved = provider || gptProvider;
    const { data } = await gptApi.get('/api/credits', {
        headers: { 'X-GPT-Provider': resolved },
        params: { provider: resolved },
    });
    return data;
}

export async function fetchSettings() {
    const { data } = await gptApi.get('/api/settings');
    return data;
}

export async function patchSettings(payload) {
    const { data } = await gptApi.patch('/api/settings', payload);
    return data;
}

export async function uploadFile(file, { chatId, onProgress, signal } = {}) {
    const form = new FormData();
    form.append('file', file);
    if (chatId) form.append('chat_id', chatId);
    const { data } = await gptApi.post('/api/files/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        onUploadProgress: (evt) => {
            if (!onProgress || !evt.total) return;
            onProgress({
                loaded: evt.loaded,
                total: evt.total,
                percent: Math.round((evt.loaded / evt.total) * 100),
            });
        },
    });
    return data;
}

export async function deleteFile(fileId) {
    const { data } = await gptApi.delete(`/api/files/${fileId}`);
    return data;
}

export async function generateImage(payload) {
    const { data } = await gptApi.post('/api/images/generate', payload);
    return data;
}

export async function fetchAudioModels() {
    const { data } = await gptApi.get('/api/audio-models');
    return data;
}

export async function fetchVideoModels() {
    const { data } = await gptApi.get('/api/video-models');
    return data;
}

export async function generateSpeech(payload) {
    const { data } = await gptApi.post('/api/audio/speech', payload);
    return data;
}

export async function transcribeAudio(payload) {
    const { data } = await gptApi.post('/api/audio/transcribe', payload);
    return data;
}

export async function generateVideo(payload) {
    const { data } = await gptApi.post('/api/videos/generate', payload);
    return data;
}

export async function fetchVideoStatus(messageId) {
    const { data } = await gptApi.get('/api/videos/status', {
        params: { message_id: messageId },
    });
    return data;
}

export async function stopGeneration(chatId, messageId) {
    const { data } = await gptApi.post(`/api/chats/${chatId}/stop`, {
        message_id: messageId || null,
    });
    return data;
}

/**
 * SSE stream via fetch + AbortController.
 * onEvent({event, data})
 */
export async function streamChatMessage(chatId, payload, { signal, onEvent }) {
    const token = localStorage.getItem('token');
    const response = await fetch(
        `${API_URL}/api/chats/${chatId}/messages/stream?provider=${encodeURIComponent(gptProvider)}`,
        {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: token ? `Bearer ${token}` : '',
            'X-GPT-Provider': gptProvider,
        },
        body: JSON.stringify(payload),
        signal,
    });

    if (!response.ok) {
        let errBody = null;
        try {
            errBody = await response.json();
        } catch (_) {
            /* ignore */
        }
        const error = errBody?.error || errBody?.detail || {
            code: 'PROVIDER_ERROR',
            message: `HTTP ${response.status}`,
            details: {},
        };
        throw Object.assign(new Error(error.message || 'Stream failed'), { gptError: error });
    }

    await readSse(response, onEvent);
}

export async function streamRegenerate(chatId, payload = {}, { signal, onEvent }) {
    const token = localStorage.getItem('token');
    const response = await fetch(
        `${API_URL}/api/chats/${chatId}/regenerate?provider=${encodeURIComponent(gptProvider)}`,
        {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: token ? `Bearer ${token}` : '',
            'X-GPT-Provider': gptProvider,
        },
        body: JSON.stringify(payload),
        signal,
    });
    if (!response.ok) {
        let errBody = null;
        try {
            errBody = await response.json();
        } catch (_) {
            /* ignore */
        }
        const error = errBody?.error || errBody?.detail || {
            code: 'PROVIDER_ERROR',
            message: `HTTP ${response.status}`,
            details: {},
        };
        throw Object.assign(new Error(error.message || 'Stream failed'), { gptError: error });
    }
    await readSse(response, onEvent);
}

async function readSse(response, onEvent) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let eventName = 'message';
    let dataLines = [];

    const flush = () => {
        if (!dataLines.length) return;
        const raw = dataLines.join('\n');
        dataLines = [];
        let data = raw;
        try {
            data = JSON.parse(raw);
        } catch (_) {
            /* keep string */
        }
        onEvent?.({ event: eventName || 'message', data });
        eventName = 'message';
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop() || '';
        for (const line of parts) {
            const trimmed = line.replace(/\r$/, '');
            if (!trimmed) {
                flush();
                continue;
            }
            if (trimmed.startsWith(':')) continue;
            if (trimmed.startsWith('event:')) {
                eventName = trimmed.slice(6).trim();
                continue;
            }
            if (trimmed.startsWith('data:')) {
                dataLines.push(trimmed.slice(5).trim());
            }
        }
    }
    flush();
}

export default gptApi;
